import {
  Label,
  Input,
  useToasterContext,
} from "@tandemdiabetes/sugarmate-react-components"
import type { ChangeEvent } from "react"
import {
  useCallback,
  useState,
} from "react"
import styles from "./AccountDetails.module.scss"
import {
  useOverlayLoader,
  usePolyglot,
} from "src/contexts"
import {
  useUpdateAccount,
  useAccountProperty,
} from "src/hooks"
import { EditLastnameErrorToast } from "src/toasts/EditLastnameErrorToast"
import { LastnameCannotBeEmptyErrorToast } from "src/toasts/LastnameCannotBeEmptyErrorToast"

const polyglotPrefix = "pages.account.account_details.lastname."
const lastnameId = "last-name-input"

export function Lastname() {
  const polyglot = usePolyglot()
  const { showToast } = useToasterContext()
  const { setIsOverlayLoaderVisible } = useOverlayLoader()

  const updateAccount = useUpdateAccount()

  const accountId = useAccountProperty(
    "id",
    NaN,
  )
  const currentLastname = useAccountProperty(
    "lastname",
    "",
  )

  const [
    lastname,
    setLastname,
  ] = useState<string>(currentLastname)

  const onChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setLastname(e.target.value)
    },
    [setLastname],
  )

  const resetLastname = useCallback(
    () => {
      setLastname(currentLastname)
    },
    [currentLastname],
  )

  const handleLastnameUpdate = useCallback(
    async () => {
      if (lastname === currentLastname) {
        return
      }

      // Last name cannot be left blank
      if (!lastname) {
        resetLastname()

        showToast(
          "lastname-cannot-be-empty-error",
          LastnameCannotBeEmptyErrorToast,
        )
        return
      }

      setIsOverlayLoaderVisible(true)

      try {
        if (!updateAccount) {
          throw new Error("Update account function is not available")
        }

        await updateAccount({
          id: accountId,
          lastname,
        })
      } catch {
        showToast(
          "edit-lastname-error",
          EditLastnameErrorToast,
        )
      }

      setIsOverlayLoaderVisible(false)
    },
    [
      lastname,
      currentLastname,
      accountId,
      updateAccount,
      resetLastname,
      showToast,
      setIsOverlayLoaderVisible,
    ],
  )

  const onBlur = useCallback(
    () => {
      void handleLastnameUpdate()
    },
    [handleLastnameUpdate],
  )

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        void handleLastnameUpdate()
      }
    },
    [handleLastnameUpdate],
  )

  return (
    <div>
      <Label
        className={styles.inputLabel}
        htmlFor={lastnameId}
      >
        {polyglot.t(`${polyglotPrefix}label`)}
      </Label>
      <Input
        className={styles.lastnameInput}
        id={lastnameId}
        value={lastname}
        onChange={onChange}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        autoComplete="family-name"
      />
    </div>
  )
}
